import { Injectable } from '@angular/core';
import { User } from '@pixomaticc/ngx-ui';
import { FREE_TRIAL_DAYS } from '../free-trial';

export enum OnboardingState {
  /** Account < 1 day old, never subscribed, nothing uploaded — full welcome experience. */
  NEW_USER = 'NEW_USER',
  /** Inside the free-trial window, never subscribed, nothing uploaded — show first-steps checklist. */
  IN_TRIAL_NOT_ACTIVATED = 'IN_TRIAL_NOT_ACTIVATED',
  /** Has active galleries with files (or completed the flow this session) — no onboarding UI. */
  ACTIVATED = 'ACTIVATED',
  /** Never subscribed, past the trial window, nothing uploaded — existing trial-expired messaging covers this. */
  DORMANT = 'DORMANT',
  /** Subscribed at least once — a proven user; never show first-timer onboarding regardless of upload count. */
  EVER_SUBSCRIBED = 'EVER_SUBSCRIBED',
}

const MILLISECONDS_IN_DAY: number = 1000 * 60 * 60 * 24;
const NEW_USER_MAX_AGE_DAYS: number = 1;

const FIRST_GALLERY_DONE_KEY: string = 'pixo_first_gallery_done';
const FIRST_STEPS_DISMISSED_KEY: string = 'pixo_first_steps_dismissed';
const WELCOME_SEEN_KEY_PREFIX: string = 'pixo_welcome_seen_';

// BACKEND CONTRACTS this service relies on:
// 1. totalUploadsSize is seeded to 0 at signup and only grows past 0 once files are
//    uploaded into a gallery (files can only be uploaded into a gallery), so
//    totalUploadsSize > 0 ⇔ "has active galleries with files". It drops back to 0
//    when storage is auto-cleared (90 days unsubscribed) or everything is deleted.
// 2. billing/billingDate/currency/cycle/nextBillingDate are set on first subscription
//    and PERSIST after it lapses, so !!billing ⇔ "ever subscribed".
@Injectable({ providedIn: 'root' })
export class OnboardingStateService {

  public getState(user: User): OnboardingState {
    if (this.isActivated(user)) {
      return OnboardingState.ACTIVATED;
    }

    if (!!user.billing) {
      return OnboardingState.EVER_SUBSCRIBED;
    }

    const accountAgeDays: number = this.accountAgeDays(user);

    if (accountAgeDays < NEW_USER_MAX_AGE_DAYS) {
      return OnboardingState.NEW_USER;
    }

    if (accountAgeDays < FREE_TRIAL_DAYS) {
      return OnboardingState.IN_TRIAL_NOT_ACTIVATED;
    }

    return OnboardingState.DORMANT;
  }

  /**
   * localStorage flag = instant, same-session signal (totalUploadsSize on the JWT
   * only refreshes on the next /home mount); totalUploadsSize = durable, cross-device.
   */
  public isActivated(user: User): boolean {
    return (user.totalUploadsSize ?? 0) > 0
      || localStorage.getItem(FIRST_GALLERY_DONE_KEY) === 'true';
  }

  /**
   * Called from the upload pipeline the moment files are saved into a gallery.
   * Static (localStorage only) so deep component hierarchies can call it without DI.
   */
  public static markGalleryActivated(): void {
    localStorage.setItem(FIRST_GALLERY_DONE_KEY, 'true');
  }

  public shouldShowWelcome(user: User): boolean {
    return this.getState(user) === OnboardingState.NEW_USER
      && !localStorage.getItem(WELCOME_SEEN_KEY_PREFIX + this.userKey(user));
  }

  public markWelcomeSeen(user: User): void {
    localStorage.setItem(WELCOME_SEEN_KEY_PREFIX + this.userKey(user), 'true');
  }

  public shouldShowFirstSteps(user: User): boolean {
    if (localStorage.getItem(FIRST_STEPS_DISMISSED_KEY) === 'true') {
      return false;
    }

    const state: OnboardingState = this.getState(user);

    // Keep showing the (completed) checklist to activated users still inside the
    // trial window so they get the "all set" moment, until they dismiss it.
    return state === OnboardingState.NEW_USER
      || state === OnboardingState.IN_TRIAL_NOT_ACTIVATED
      || (state === OnboardingState.ACTIVATED && !user.billing && this.accountAgeDays(user) < FREE_TRIAL_DAYS);
  }

  public dismissFirstSteps(): void {
    localStorage.setItem(FIRST_STEPS_DISMISSED_KEY, 'true');
  }

  public accountAgeDays(user: User): number {
    return Math.floor((Date.now() - new Date(user.createdAt).getTime()) / MILLISECONDS_IN_DAY);
  }

  private userKey(user: User): string {
    return (user as unknown as { userId?: string }).userId ?? user._id ?? 'anon';
  }
}
